import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government usability testing resources',
    cards: [
      {
        title: 'Learn about writing in plain language ',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
      {
        title: 'IBM Web Accessibility Checklist',
        subtitle: '[ibm.com]',
        linkUrl: REFERENCE_LINKS.ibmAccessibilityChecklist,
      },
      {
        title: 'Government usability testing resources',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
    ],
  },
  {
    id: 2,
    title: 'Non-government accessibility resources',
    cards: [
      {
        title: 'Web Accessibility Perspectives Videos ',
        subtitle: '[w3.org]',
        linkUrl: REFERENCE_LINKS.w3cPerspectiveVideos,
      },
      {
        title: 'Inclusive design principles',
        subtitle: '[inclusivedesignprinciples.info]',
        linkUrl: REFERENCE_LINKS.inclusiveDesignPrinciples,
      },
      {
        title: 'Accessibility fundamentals with Rob Dodson ',
        subtitle: '[youtube.com]',
        linkUrl: REFERENCE_LINKS.robDodsonAccessibilityFundamentals,
      },
    ],
  },
];

const VoiceAndToneStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Voice & Tone"
        breadcrumbs={[{ label: 'Get Started', url: '/' }, { label: 'Voice & Tone' }]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="definition">
                  Definition
                </h3>
                <p className="mb-40">
                  DHCS’s voice is a reflection of who we are—it’s authentic, approachable, and
                  professional. This consistent voice allows us to communicate in a way that feels
                  familiar and reliable to our users. However, tone adapts based on the context and
                  the user’s needs. While the voice remains steady, the tone may shift to be more
                  supportive, formal, friendly, or motivating, depending on the situation and
                  audience. By adjusting our tone appropriately, we create a personalized experience
                  that feels relevant and engaging to every user.
                </p>
                <h3 className="h3" id="dhcs-s-voice">
                  DHCS’s voice
                </h3>
                <p>Our voice is a reflection of who we are. So who are we?</p>
                <p>
                  Simply put, we’re an organization built by real people who have a deep
                  understanding of this business and a genuine desire to help others succeed. Our
                  team is passionate about creating meaningful, impactful solutions that make a
                  difference in the lives of our users. We’re not just here to provide a product or
                  service; we’re here to build lasting relationships based on trust, reliability,
                  and support.
                </p>
                <p className="mb-40">
                  When someone interacts with services, we want them to feel like they’re not just
                  dealing with a state agency, but with a team of people who care about their needs,
                  challenges, and goals. Our voice reflects this—it&apos;s warm, approachable, and
                  professional. It’s clear, confident, and most importantly, it conveys our
                  commitment to empowering others and helping them thrive. Every time someone uses
                  our products or services, they should experience not just functionality, but also
                  the assurance that they are supported by a team that genuinely cares.
                </p>
              </div>
              <h2 className="divider h1" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="best-practices">
                  Best Practices
                </h3>
                <p className="mb-40">As DHCS&apos;s voice, we should always:</p>
                <p>
                  <strong>Be real, but not too tough or overly familiar</strong>
                </p>
                <ul className="disc-list">
                  <li>
                    Use business casual language—be plain-spoken, not pretentious or overly playful
                  </li>
                  <li>Be upfront and honest with users, even if we make a mistake</li>
                  <li>Communicate with clarity and empathy—be direct, not insensitive</li>
                </ul>
                <p>
                  <strong>Be dynamic, but not scattered or impulsive</strong>
                </p>
                <ul className="disc-list">
                  <li>Communicate in a way that respects our users’ emotion and context</li>
                  <li>
                    Avoid words that generalize success like &quot;every,&quot; &quot;all,&quot; and
                    &quot;most&quot;
                  </li>
                  <li>Use language that supports action, movement, and progress</li>
                </ul>
                <div className="visually-hidden" aria-hidden="true" id="adapting-tone">
                  Adapting tone
                </div>
                <h3 className="h3">Adapting tone by situation</h3>
                <p>
                  Our tone adjusts to fit the context, ensuring that our communication feels
                  relevant and appropriate for the situation. While it’s tempting to base tone
                  entirely on the perceived emotional state of the audience, the truth is that we
                  can never fully know how someone feels. Even in seemingly positive moments,
                  assumptions about emotions can be misleading.
                </p>
                <p className="mb-40">
                  Rather than dictating how our audience should feel, we focus on the context of the
                  interaction. This approach allows us to craft a tone that is thoughtful,
                  considerate, and aligned with the needs of the moment. Below are some common
                  situations where tone adaptation can make a meaningful difference.
                </p>
                <div className="visually-hidden" aria-hidden="true" id="everyday-tasks">
                  Everyday tasks
                </div>
                <h3 className="h3">Everyday tasks and activities</h3>
                <p>
                  For everyday tasks and activities, our tone remains consistent, ensuring clarity
                  and ease of use. While these interactions may seem routine, they are essential for
                  maintaining a seamless user experience. Instead of overloading users with
                  unnecessary information or complexity, we focus on straightforward, actionable
                  language that guides them efficiently through their tasks.
                </p>
                <p className="mb-40">
                  By prioritizing simplicity and relevance, we make everyday activities feel
                  intuitive and stress-free. Whether it’s filling out a form, navigating a menu, or
                  completing a purchase, our tone supports the user without overstepping or
                  complicating the process.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-everyday-tasks-01@2x.webp"
                    width={488}
                    height={276}
                    alt="Look for the simplest way to communicate information—words aren’t always
                      necessary"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      Look for the simplest way to communicate information—words aren’t always
                      necessary.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-everyday-tasks-02@2x.webp"
                    width={488}
                    height={276}
                    alt="Use overly complicated or intimidating language"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Use overly complicated or intimidating language.</p>
                  </div>
                </div>
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-everyday-tasks-03@2x.webp"
                    width={488}
                    height={385}
                    alt="Be consistent for identical actions or destinations when possible"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Be consistent for identical actions or destinations when possible.</p>
                    <ul className="disc-list">
                      <li>Remove measure</li>
                      <li>Delete collection</li>
                    </ul>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-everyday-tasks-04@2x.webp"
                    width={488}
                    height={385}
                    alt="Add extra text just to fill space"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Add extra text just to fill space.</p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="simple-errors">
                  Simple errors
                </h3>
                <p>
                  For simple errors, our tone should remain calm, clear, and solution-focused.
                  Mistakes happen, and users should feel supported, not frustrated, when
                  encountering an issue. Instead of overwhelming them with technical jargon or
                  complex explanations, we provide concise, actionable steps to resolve the problem.
                </p>
                <p className="mb-40">
                  By acknowledging the error and offering straightforward guidance, we help users
                  quickly recover and continue their tasks with confidence. A reassuring and
                  empathetic tone ensures that simple errors don’t become significant roadblocks in
                  their experience.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-simple-errors-01@2x.webp"
                    width={488}
                    height={368}
                    alt="Look for the simplest way to communicate information—words aren't
                      always necessary"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      Look for the simplest way to communicate information—words aren&apos;t always
                      necessary.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-simple-errors-02@2x.webp"
                    width={488}
                    height={368}
                    alt="Use overly complicated or intimidating language"
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>Use overly complicated or intimidating language.</p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="announce">
                  Announce
                </div>
                <h3 className="h3">Announcing new features and updates</h3>
                <p>
                  When announcing new features and updates, our tone should be engaging,
                  informative, and optimistic. These communications are an opportunity to excite
                  users about the improvements we’ve made while clearly conveying the value these
                  changes bring to their experience.
                </p>
                <p>
                  We focus on highlighting how the updates solve user challenges, enhance
                  functionality, or introduce new possibilities, using straightforward and
                  accessible language. By maintaining a balance between enthusiasm and clarity, we
                  ensure users understand the benefits without feeling overwhelmed.
                </p>
                <p className="mb-40">
                  Our goal is to inspire curiosity, foster trust, and encourage users to explore the
                  latest enhancements confidently.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>Pitch our product by explaining the specific merchant benefits.</p>
                    <p>
                      <strong>Introducing the new Map Charts Updates</strong>
                    </p>
                    <p>
                      Make it your own with custom fonts, colors, and styles. It also
                      <br />
                      loads 50% faster so your visitors won&apos;t miss it.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>
                      Just talk about ourselves and assume the experience for merchants will be
                      easy.
                    </p>
                    <p>
                      <strong>Get DHCS’s new Map Charts Updates</strong>
                    </p>
                    <p>
                      It&rsquo;s never been easier to customize the fonts, colors, and styles of
                      <br />
                      new buttons. It&rsquo;s also twice as fast.
                    </p>
                  </div>
                </div>
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-announce-01@2x.webp"
                    width={488}
                    height={338}
                    alt="If possible, let users dismiss announcements or postpone
                        changes until they're ready."
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      If possible, let users dismiss announcements or postpone changes until
                      they&apos;re ready.
                    </p>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <h3 className="h3" id="social">
                  Social
                </h3>
                <p>
                  For social interactions, our tone should be authentic, approachable, and
                  conversational. Social platforms are spaces for connection, so our communication
                  should feel less formal while still reflecting professionalism and care.
                </p>
                <p>
                  We aim to engage with users in a way that builds relationships, whether by
                  responding to questions, joining discussions, or sharing updates. Humor and warmth
                  can be appropriate when aligned with the context, but we always prioritize
                  inclusivity and respect.
                </p>
                <p className="mb-40">
                  By staying true to our values and fostering a sense of community, our social tone
                  helps create meaningful and lasting connections with our audience.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-social-01@2x.webp"
                    width={488}
                    height={432}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-socials-02@2x.webp"
                    width={488}
                    height={432}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                  </div>
                </div>
              </div>
              <div className="page-content-item">
                <div className="visually-hidden" aria-hidden="true" id="podcast">
                  Podcast
                </div>
                <h3 className="h3">Podcast or Webcast</h3>
                <p>
                  For podcasts or webcasts, our tone is conversational, engaging, and approachable.
                  We aim to deliver valuable content clearly and relatably, using real-world
                  examples and anecdotes to maintain interest.
                </p>
                <p className="mb-40">
                  Balancing professionalism with personality, we ensure our insights are accessible
                  and enjoyable, creating an experience that informs and connects with the audience.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-podcast-01@2x.webp"
                    width={488}
                    height={432}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/get-started-voice-and-tone/screen-podcast-02@2x.webp"
                    width={488}
                    height={432}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                  </div>
                </div>
              </div>
              <h2 className="divider h1" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'definition',
                    'dhcs-s-voice',
                    'guidelines',
                    'best-practices',
                    'adapting-tone',
                    'everyday-tasks',
                    'simple-errors',
                    'announce',
                    'social',
                    'podcast',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/get-started/content"
        previous="Content"
        nextLink="/branding-styleguide"
        next="Branding Styleguide"
      />
    </div>
  </div>
);

const VoiceAndTone = styled(VoiceAndToneStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 5, 6, 7, 8, 9, 10, 11])}
    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default VoiceAndTone;
